class ApiService {
  constructor() {
    // API URL - auto-detect from hostname or use environment variable
    this.baseUrl = this.detectApiUrl();
    // Token priority: localStorage > runtime config > none
    this.token = localStorage.getItem("apiToken") || window.APP_CONFIG?.GEMINI_API_KEY || null;

    console.log("[ApiService] Initialized with baseUrl:", this.baseUrl);
    console.log("[ApiService] VITE_GEMINI_SERVICE_URL env var:", import.meta.env.VITE_GEMINI_SERVICE_URL);
    console.log("[ApiService] Token source:", localStorage.getItem("apiToken") ? "localStorage" : (window.APP_CONFIG?.GEMINI_API_KEY ? "APP_CONFIG" : "none"));

    // Check API health on initialization
    this.checkInitialHealth();
  }

  detectApiUrl() {
    // If env var is set, use it
    if (import.meta.env.VITE_GEMINI_SERVICE_URL) {
      return import.meta.env.VITE_GEMINI_SERVICE_URL;
    }

    // Auto-detect based on hostname
    const hostname = window.location.hostname;

    // Production environments on trackvision.ai
    if (hostname.includes('.trackvision.ai')) {
      const env = hostname.split('.')[0]; // e.g., "gemini-ui.devmaster" -> "gemini-ui"
      const parts = hostname.split('.');
      // Handle both "gemini-ui.devmaster.trackvision.ai" and "devmaster.trackvision.ai"
      const envName = parts.length >= 3 ? parts[parts.length - 3] : parts[0];
      return `https://gemini.${envName}.trackvision.ai`;
    }

    // Default for local development
    return "http://localhost:8081";
  }

  async checkInitialHealth() {
    try {
      // Only check health if we have a token
      if (!this.token) {
        return;
      }

      await this.checkHealth();
    } catch (error) {
      // Initial health check can fail when offline - log but don't alert user
      console.warn('Initial health check failed:', error.message);
    }
  }

  getHeaders() {
    if (!this.token) {
      throw new Error("API Token not configured. Please check your settings.");
    }
    const headers = new Headers();
    headers.append("accept", "application/json");
    headers.append("content-type", "application/json");
    headers.append("Authorization", `Bearer ${this.token}`);
    return headers;
  }

  validateSettings() {
    if (!this.baseUrl) {
      const error = "API Endpoint not configured. Please check your settings.";
      throw new Error(error);
    }

    if (!this.token) {
      const error = "API Token not configured. Please check your settings.";
      throw new Error(error);
    }

    try {
      new URL(this.baseUrl);
    } catch (error) {
      const errorMsg = "Invalid API Endpoint URL. Please check your settings.";
      throw new Error(errorMsg);
    }
  }

  /**
   * Get or generate a persistent user ID for anonymous users
   * @returns {string} UUID stored in localStorage
   */
  getUserId() {
    let userId = localStorage.getItem('userId');
    if (!userId) {
      userId = crypto.randomUUID();
      localStorage.setItem('userId', userId);
      console.log('[ApiService] Generated new userId:', userId);
    }
    return userId;
  }

  async checkHealth() {
    try {
      this.validateSettings();

      const requestOptions = {
        method: "GET",
        headers: this.getHeaders(),
        redirect: "follow",
      };

      const response = await fetch(`${this.baseUrl}/health`, requestOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response
        .text()
        .then((text) => (text ? JSON.parse(text) : {}));

      // Validate the health response format
      if (!data.status || data.status !== "ok") {
        throw new Error("Invalid health check response");
      }

      return data;
    } catch (error) {
      console.error('API health check failed:', error);
      throw new Error(`API health check failed: ${error.message}`);
    }
  }

  async createThread() {
    try {
      this.validateSettings();

      // Get the selected role UUID from localStorage
      const selectedRoleUuid = localStorage.getItem("selectedRoleUuid");
      // Get the debug flag from localStorage
      const isDebug = localStorage.getItem("debug") === "true";

      let requestOptions = {
        method: "POST",
        headers: this.getHeaders(),
        redirect: "follow",
      };

      // Prepare the body object - always include userId
      const requestBody = {
        userId: this.getUserId(),
      };

      // Add debug flag if true
      if (isDebug) {
        requestBody.debug = true;
      }

      // If a role is selected, include it in the request body
      if (selectedRoleUuid) {
        requestBody.role = selectedRoleUuid; // Add role to the body object
      }

      // Always send body (userId is always present)
      requestOptions.body = JSON.stringify(requestBody);

      const response = await fetch(`${this.baseUrl}/threads`, requestOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const rawText = await response.text();
      const data = rawText ? JSON.parse(rawText) : {};

      // Return the threadId directly without any prefix modification
      return data;
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          `Failed to connect to ${this.baseUrl}. Please check your API endpoint and network connection.`
        );
      }
      throw error;
    }
  }

  async sendMessage(threadId, message) {
    try {
      this.validateSettings();

      // Get the selected role UUID from localStorage
      const selectedRoleUuid = localStorage.getItem("selectedRoleUuid");

      // Prepare request body with message and role if selected
      const requestBody = {
        message: message,
      };

      // Only add role if it exists and is not empty
      if (selectedRoleUuid) {
        requestBody.role = selectedRoleUuid;
      }

      const requestOptions = {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody),
        redirect: "follow",
      };

      const response = await fetch(
        `${this.baseUrl}/threads/${threadId}`,
        requestOptions
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response
        .text()
        .then((text) => (text ? JSON.parse(text) : {}));

      return responseData;
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          `Failed to connect to ${this.baseUrl}. Please check your API endpoint and network connection.`
        );
      }
      throw error;
    }
  }

  updateSettings(newToken) {
    // Base URL comes from environment variable only - not user configurable
    if (newToken) {
      localStorage.setItem("apiToken", newToken);
      this.token = newToken;
    } else {
      // Token priority: localStorage > runtime config
      this.token = localStorage.getItem("apiToken") || window.APP_CONFIG?.GEMINI_API_KEY || null;
    }

    try {
      this.validateSettings();
      return { success: true, baseUrl: this.baseUrl, token: this.token };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Add a method to force refresh settings from localStorage
  refreshSettings() {
    // Base URL comes from environment variable only
    // Token priority: localStorage > runtime config
    this.token = localStorage.getItem("apiToken") || window.APP_CONFIG?.GEMINI_API_KEY || null;
    return { baseUrl: this.baseUrl, token: this.token };
  }

  async get(endpoint) {
    try {
      this.validateSettings();

      const requestOptions = {
        method: "GET",
        headers: this.getHeaders(),
        redirect: "follow",
      };

      const response = await fetch(
        `${this.baseUrl}${endpoint}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response
        .text()
        .then((text) => (text ? JSON.parse(text) : {}));
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          `Failed to connect to ${this.baseUrl}. Please check your API endpoint and network connection.`
        );
      }
      throw error;
    }
  }

  async getRoles() {
    try {
      return await this.get("/roles");
    } catch (error) {
      // Return an object with an empty roles array as fallback
      return { roles: [] };
    }
  }

  async getRoleDetails(uuid) {
    try {
      if (!uuid) {
        throw new Error("Role UUID is required");
      }
      return await this.get(`/roles/${uuid}`);
    } catch (error) {
      return null;
    }
  }

  async getContexts() {
    try {
      return await this.get("/contexts");
    } catch (error) {
      // Return an object with an empty contexts array as fallback
      return { contexts: [] };
    }
  }

  /**
   * Get all threads with pagination for the current user
   * @param {number} limit - Max threads to return (default 50)
   * @param {number} offset - Pagination offset (default 0)
   * @param {string|null} status - Filter by status ('active', 'archived')
   * @returns {Promise<{threads: Array, total: number, limit: number, offset: number}>}
   */
  async getThreads(limit = 50, offset = 0, status = null) {
    try {
      const userId = this.getUserId();
      let endpoint = `/threads?userId=${userId}&limit=${limit}&offset=${offset}`;
      if (status) {
        endpoint += `&status=${status}`;
      }
      return await this.get(endpoint);
    } catch (error) {
      console.error('[ApiService] getThreads failed:', error);
      throw error;
    }
  }

  /**
   * Get a single thread with all its messages
   * @param {string} threadId - The external thread ID
   * @returns {Promise<Object>} Thread object with messages array
   */
  async getThread(threadId) {
    try {
      if (!threadId) {
        throw new Error("Thread ID is required");
      }
      return await this.get(`/threads/${threadId}`);
    } catch (error) {
      console.error('[ApiService] getThread failed:', error);
      throw error;
    }
  }

  /**
   * Delete a thread permanently
   * @param {string} threadId - The external thread ID
   * @returns {Promise<boolean>} true on success
   */
  async deleteThread(threadId) {
    try {
      this.validateSettings();

      if (!threadId) {
        throw new Error("Thread ID is required");
      }

      const requestOptions = {
        method: "DELETE",
        headers: this.getHeaders(),
        redirect: "follow",
      };

      const response = await fetch(
        `${this.baseUrl}/threads/${threadId}`,
        requestOptions
      );

      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return true;
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          `Failed to connect to ${this.baseUrl}. Please check your API endpoint and network connection.`
        );
      }
      throw error;
    }
  }

  /**
   * Update a thread (e.g., toggle like status)
   * @param {string} threadId - The external thread ID
   * @param {Object} updates - Fields to update (e.g., { isLiked: true })
   * @returns {Promise<Object>} Updated thread data
   */
  async updateThread(threadId, updates) {
    try {
      this.validateSettings();

      if (!threadId) {
        throw new Error("Thread ID is required");
      }

      const requestOptions = {
        method: "PATCH",
        headers: this.getHeaders(),
        body: JSON.stringify(updates),
        redirect: "follow",
      };

      const response = await fetch(
        `${this.baseUrl}/threads/${threadId}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response
        .text()
        .then((text) => (text ? JSON.parse(text) : {}));
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          `Failed to connect to ${this.baseUrl}. Please check your API endpoint and network connection.`
        );
      }
      throw error;
    }
  }

  async interpretThread(threadId, previousResults) {
    try {
      this.validateSettings();

      const requestOptions = {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({ results: previousResults }),
        redirect: "follow",
      };

      const response = await fetch(
        `${this.baseUrl}/interpret/${threadId}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response
        .text()
        .then((text) => (text ? JSON.parse(text) : {}));
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          `Failed to connect to ${this.baseUrl}. Please check your API endpoint and network connection.`
        );
      }
      throw error;
    }
  }

  // ==========================================
  // Feedback API Methods
  // ==========================================

  /**
   * Submit feedback for a response
   * @param {Object} data - Feedback data
   * @param {string} data.threadId - Thread ID
   * @param {string} data.feedbackType - 'positive' or 'negative'
   * @param {string} data.question - Original question
   * @param {string} data.reason - User's explanation
   * @param {string} data.sqlQuery - SQL query that was executed
   * @param {Object} data.responseData - Full API response
   * @returns {Promise<Object>} Created feedback object
   */
  async submitFeedback(data) {
    try {
      this.validateSettings();

      const requestBody = {
        userId: this.getUserId(),
        threadId: data.threadId,
        feedbackType: data.feedbackType,
        question: data.question,
        reason: data.reason || '',
        sqlQuery: data.sqlQuery || '',
        responseData: data.responseData,
        archived: false
      };

      const requestOptions = {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody),
        redirect: 'follow'
      };

      const response = await fetch(`${this.baseUrl}/feedback`, requestOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.text().then(text => text ? JSON.parse(text) : {});
    } catch (error) {
      console.error('[ApiService] submitFeedback failed:', error);
      throw error;
    }
  }

  /**
   * Get all feedback for the current user
   * @param {Object} options - Query options
   * @param {string} options.threadId - Filter by thread ID (optional)
   * @param {number} options.limit - Max items to return (default 50)
   * @param {number} options.offset - Pagination offset (default 0)
   * @returns {Promise<{feedback: Array, total: number, limit: number, offset: number}>}
   */
  async listFeedback(options = {}) {
    try {
      const userId = this.getUserId();
      const { threadId, limit = 50, offset = 0, archived } = options;

      let endpoint = `/feedback?userId=${userId}&limit=${limit}&offset=${offset}`;
      if (threadId) {
        endpoint += `&threadId=${threadId}`;
      }
      if (archived !== undefined) {
        endpoint += `&archived=${archived}`;
      }

      return await this.get(endpoint);
    } catch (error) {
      console.error('[ApiService] listFeedback failed:', error);
      // Return empty feedback array on error for graceful degradation
      return { feedback: [], total: 0, limit: 50, offset: 0 };
    }
  }

  /**
   * Delete a specific feedback entry
   * @param {string} feedbackId - Feedback UUID to delete
   * @returns {Promise<boolean>} true on success
   */
  async deleteFeedback(feedbackId) {
    try {
      this.validateSettings();

      if (!feedbackId) {
        throw new Error('Feedback ID is required');
      }

      const requestOptions = {
        method: 'DELETE',
        headers: this.getHeaders(),
        redirect: 'follow'
      };

      const response = await fetch(`${this.baseUrl}/feedback/${feedbackId}`, requestOptions);

      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('[ApiService] deleteFeedback failed:', error);
      throw error;
    }
  }

  /**
   * Clear all feedback for the current user
   * @returns {Promise<{deletedCount: number, message: string}>}
   */
  async clearFeedback() {
    try {
      this.validateSettings();

      const userId = this.getUserId();

      const requestOptions = {
        method: 'DELETE',
        headers: this.getHeaders(),
        redirect: 'follow'
      };

      const response = await fetch(`${this.baseUrl}/feedback?userId=${userId}`, requestOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.text().then(text => text ? JSON.parse(text) : { deleted_count: 0 });
    } catch (error) {
      console.error('[ApiService] clearFeedback failed:', error);
      throw error;
    }
  }

  // ==========================================
  // Optimize & Custom Context API Methods
  // ==========================================

  /**
   * Generate optimized context from feedback
   * @returns {Promise<Object>} Optimization result with generated context
   */
  async generateOptimizedContext() {
    try {
      this.validateSettings();

      const requestOptions = {
        method: 'POST',
        headers: this.getHeaders(),
        redirect: 'follow'
      };

      const response = await fetch(`${this.baseUrl}/optimize`, requestOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.text().then(text => text ? JSON.parse(text) : {});
    } catch (error) {
      console.error('[ApiService] generateOptimizedContext failed:', error);
      throw error;
    }
  }

  /**
   * Get current optimized context
   * @param {string|null} type - Optimization type ('sql' or 'interpretation')
   * @returns {Promise<Object>} Current optimized context
   */
  async getOptimizedContext(type = null) {
    try {
      let endpoint = '/optimize';
      if (type) {
        endpoint += `?type=${type}`;
      }
      return await this.get(endpoint);
    } catch (error) {
      console.error('[ApiService] getOptimizedContext failed:', error);
      return { content: '' };
    }
  }

  /**
   * Save optimized context
   * @param {string} content - Markdown content to save
   * @param {string|null} type - Optimization type ('sql' or 'interpretation')
   * @returns {Promise<Object>} Saved context
   */
  async saveOptimizedContext(content, type = null) {
    try {
      this.validateSettings();

      let endpoint = '/optimize';
      if (type) {
        endpoint += `?type=${type}`;
      }

      const requestOptions = {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({ content }),
        redirect: 'follow'
      };

      const response = await fetch(`${this.baseUrl}${endpoint}`, requestOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.text().then(text => text ? JSON.parse(text) : {});
    } catch (error) {
      console.error('[ApiService] saveOptimizedContext failed:', error);
      throw error;
    }
  }

  /**
   * Get custom context
   * @returns {Promise<Object>} Custom context content
   */
  async getCustomContext() {
    try {
      return await this.get('/context/custom');
    } catch (error) {
      console.error('[ApiService] getCustomContext failed:', error);
      return { content: '' };
    }
  }

  /**
   * Create or update custom context
   * @param {string} content - Markdown content to save
   * @returns {Promise<Object>} Saved context
   */
  async saveCustomContext(content) {
    try {
      this.validateSettings();

      const requestOptions = {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ content }),
        redirect: 'follow'
      };

      const response = await fetch(`${this.baseUrl}/context/custom`, requestOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.text().then(text => text ? JSON.parse(text) : {});
    } catch (error) {
      console.error('[ApiService] saveCustomContext failed:', error);
      throw error;
    }
  }

  /**
   * Delete custom context
   * @returns {Promise<boolean>} true on success
   */
  async deleteCustomContext() {
    try {
      this.validateSettings();

      const requestOptions = {
        method: 'DELETE',
        headers: this.getHeaders(),
        redirect: 'follow'
      };

      const response = await fetch(`${this.baseUrl}/context/custom`, requestOptions);

      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('[ApiService] deleteCustomContext failed:', error);
      throw error;
    }
  }

  async login(email, password) {
    try {
      // Don't validate settings for login as the token might not be available yet
      if (!this.baseUrl) {
        throw new Error(
          "API Endpoint not configured. Please check your settings."
        );
      }

      // SECURITY FIX: Removed hardcoded authentication token
      // The login endpoint should not require authentication, or should use
      // a public client ID/secret configured via environment variables
      console.warn(
        "Login endpoint accessed without authentication token. " +
        "Please ensure your backend login endpoint is properly configured."
      );

      const headers = new Headers();
      headers.append("accept", "application/json");
      headers.append("content-type", "application/json");
      // Note: No Authorization header for login endpoint

      const requestOptions = {
        method: "POST",
        headers: headers,
        body: JSON.stringify({ email, password }),
        redirect: "follow",
      };

      const response = await fetch(`${this.baseUrl}/login`, requestOptions);

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP error! status: ${response.status}`);
      }

      const data = await response
        .text()
        .then((text) => (text ? JSON.parse(text) : {}));

      console.log("Login API response:", data);

      // Check for 'key' or 'token' in the response
      const authToken = data.key || data.token;

      // If login successful, store the token in localStorage
      if (authToken) {
        localStorage.setItem("apiToken", authToken);

        // Update the instance properties
        this.token = authToken;
        console.log(
          `Login successful: token saved to localStorage`
        );
      } else {
        console.error("Login response did not contain a key or token:", data);
      }

      // Return the data with normalized token field
      return { ...data, token: authToken };
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          `Failed to connect to ${this.baseUrl}. Please check your API endpoint and network connection.`
        );
      }
      throw error;
    }
  }
}

const apiService = new ApiService();
export { apiService };
export default apiService;
